import { useEffect, useState } from "react";
import { Menu, X, ChevronDown, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAV_LINKS = [
  { label: "Home", href: "#home" },
  { label: "Services", href: "#services" },
  { label: "Work", href: "#work" },
  { label: "Process", href: "#process" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (href) => {
    setOpen(false);
    const id = href.replace("#", "");
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="nav-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#0A0A0A]/85 border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <button
          onClick={() => handleNav("#home")}
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <span className="font-display text-2xl md:text-3xl tracking-tight leading-none text-white">
            <span className="italic">A</span>
            <span className="italic -ml-1.5">S</span>
          </span>
          <span className="hidden sm:block text-[10px] tracking-[0.32em] uppercase text-white/60 border-l border-white/15 pl-3">
            Almonte Sites
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {NAV_LINKS.map((link) => (
            <button
              key={link.label}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              onClick={() => handleNav(link.href)}
              className="text-sm text-white/70 hover:text-white transition-colors relative group"
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-blue-500 transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="nav-menu-dropdown-trigger"
                className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border border-white/15 hover:border-white/35 text-sm text-white/80 hover:text-white transition-colors bg-transparent"
              >
                Menu
                <ChevronDown size={14} className="opacity-70" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              sideOffset={10}
              data-testid="nav-menu-dropdown-content"
              className="w-56 bg-[#0F0F0F] border border-white/10 text-white rounded-xl p-1.5"
            >
              {NAV_LINKS.map((link) => (
                <DropdownMenuItem
                  key={link.label}
                  data-testid={`nav-menu-item-${link.label.toLowerCase()}`}
                  onSelect={() => handleNav(link.href)}
                  className="cursor-pointer rounded-md px-3 py-2 text-sm text-white/80 hover:!text-white focus:!text-white hover:!bg-white/5 focus:!bg-white/5"
                >
                  {link.label}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator className="my-1.5 bg-white/10" />
              <DropdownMenuItem
                data-testid="nav-menu-item-admin"
                asChild
                className="cursor-pointer rounded-md px-3 py-2 text-sm text-blue-300 hover:!text-blue-200 focus:!text-blue-200 hover:!bg-blue-500/10 focus:!bg-blue-500/10"
              >
                <a href="/admin" className="flex items-center gap-2">
                  <Lock size={13} />
                  Admin
                </a>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            data-testid="nav-cta-button"
            onClick={() => handleNav("#contact")}
            className="bg-blue-500 hover:bg-blue-600 text-white rounded-full px-5 h-10 font-medium"
          >
            Get a Free Review
          </Button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="md:hidden backdrop-blur-xl bg-[#0A0A0A]/95 border-b border-white/10"
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV_LINKS.map((link) => (
              <button
                key={link.label}
                data-testid={`nav-mobile-link-${link.label.toLowerCase()}`}
                onClick={() => handleNav(link.href)}
                className="text-left text-white/80 hover:text-white text-base"
              >
                {link.label}
              </button>
            ))}
            <a
              href="/admin"
              data-testid="nav-mobile-link-admin"
              className="text-left text-blue-300 hover:text-blue-200 text-base inline-flex items-center gap-2"
            >
              <Lock size={14} />
              Admin
            </a>
            <Button
              data-testid="nav-mobile-cta"
              onClick={() => handleNav("#contact")}
              className="bg-blue-500 hover:bg-blue-600 text-white rounded-full h-11 mt-2"
            >
              Get a Free Review
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
